import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  copyCampaign,
  fetchCampaign,
  finishCampaign,
  nextChapter,
  setCampaignVisibility,
  type CampaignView,
  type ChapterDelta,
} from '../api/campaigns';
import {
  fetchRecipes,
  fetchVotes,
  terrainImageUrl,
  type RecipeView,
  type VoteView,
} from '../api/missions';
import { ca } from '../i18n/ca';
import { Planned, PlannedChip } from './Planned';
import { isComing, isTesting } from './recipeRules';
import { PageLoading, PageProblem } from './PageStates';
import { useRetryKey } from './connectionHelpers';
import { Shell } from './Shell';
import { errorText } from './useSessionLive';

/** The chapter's consequences, shown on the board and on the session page after a chapter is played. */
export function DeltaList({ delta }: { delta: ChapterDelta }) {
  return (
    <ul className="plain small">
      {delta.sectors.map((s) => (
        <li key={s.name}>
          {ca.sectorsChanged}: <strong>{s.name}</strong> {ca.sectorControl[s.from]} →{' '}
          {ca.sectorControl[s.to]}
        </li>
      ))}
      <li>
        {ca.enemyStrength}: {Math.round(delta.enemyStrengthBefore * 100)}% →{' '}
        {Math.round(delta.enemyStrengthAfter * 100)}%
      </li>
      {delta.cast.map((c) => (
        <li key={c.name}>
          {c.name}: {ca.castStatus[c.from] ?? c.from} → {ca.castStatus[c.to] ?? c.to}
        </li>
      ))}
      {delta.threadsOpen.map((t) => (
        <li key={t}>
          {ca.threads}: {t}
        </li>
      ))}
      {delta.medals.map((m) => (
        <li key={m.name + m.medal}>
          {ca.medalsAwarded}: {m.name} · <strong>{m.medal}</strong>
        </li>
      ))}
    </ul>
  );
}

/** The campaign board (UI doc 5.4): the map as a scoreboard, chapters, cast, threads, standings. */
export function CampaignBoard() {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<CampaignView | null>(null);
  const [recipes, setRecipes] = useState<RecipeView[]>([]);
  const [recipe, setRecipe] = useState('');
  const [vote, setVote] = useState<VoteView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    fetchCampaign(code)
      .then((c) => {
        setCampaign(c);
        setError(null);
      })
      .catch((e) => setError(errorText(e)));
  }, [code]);

  const [attempt] = useRetryKey();
  useEffect(() => {
    load();
  }, [load, attempt]);

  useEffect(() => {
    if (campaign?.canEdit) {
      fetchRecipes()
        .then(setRecipes)
        .catch(() => setRecipes([]));
    }
  }, [campaign?.canEdit]);

  // The chapter whose session is live or scheduled: the campaign's "next session".
  const nextSessionChapter = campaign?.chapters.find(
    (c) => c.sessionCode && (c.sessionState === 'LIVE' || c.sessionState === 'SCHEDULED'),
  );
  const nextSessionCode = nextSessionChapter?.sessionCode ?? null;

  useEffect(() => {
    if (!nextSessionCode) {
      setVote(null);
      return;
    }
    fetchVotes(nextSessionCode)
      .then((vs) => setVote(vs.find((v) => v.key === 'nextChapter' && v.state === 'OPEN') ?? null))
      .catch(() => setVote(null));
  }, [nextSessionCode]);

  if (!campaign) {
    return (
      <Shell title={ca.campaignPage.kicker} width="wide">
        {error ? <PageProblem error={null} text={error} onRetry={load} /> : <PageLoading />}
      </Shell>
    );
  }
  const board = campaign.board;
  const worldSize = 20480;
  const size = 420;
  const px = (x: number) => (x / worldSize) * size;
  const py = (y: number) => size - (y / worldSize) * size;
  const last = campaign.chapters[campaign.chapters.length - 1];
  const pendingChapter = last && last.outcome === null ? last : null;
  const canGenerate = campaign.canEdit && campaign.state === 'ACTIVE' && !pendingChapter;
  const territory = board
    ? (['enemy', 'contested', 'friendly'] as const).map((k) => ({
        key: k,
        count: board.sectors.filter((s) => s.control === k).length,
      }))
    : [];

  async function generate() {
    setBusy(true);
    try {
      const m = await nextChapter(code, recipe || null);
      navigate(`/m/${m.code}`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  function share() {
    void navigator.clipboard
      ?.writeText(`${window.location.origin}/c/${code}`)
      .then(() => setCopied(true))
      .catch(() => undefined);
  }

  return (
    <Shell
      title={campaign.title}
      kicker={ca.campaignPage.kicker}
      width="wide"
      chips={
        <>
          <span className="chip">{campaign.worldDisplayName ?? campaign.worldName}</span>
          {board && <span className="chip num">{ca.day(board.day)}</span>}
          <span className="chip">
            {campaign.state === 'FINISHED' ? ca.finished : ca.chapter(campaign.chapter)}
          </span>
          {campaign.standings.length > 0 && (
            <span className="chip">{ca.campaignPage.participants(campaign.standings.length)}</span>
          )}
        </>
      }
      actions={
        <>
          <button type="button" onClick={share}>
            {copied ? ca.linkCopied : ca.campaignPage.share}
          </button>
          {campaign.canEdit && campaign.state === 'ACTIVE' && (
            <button
              type="button"
              onClick={() =>
                void finishCampaign(code)
                  .then(setCampaign)
                  .catch((e) => setError(errorText(e)))
              }
            >
              {ca.finishCampaign}
            </button>
          )}
          <button
            type="button"
            onClick={() =>
              void copyCampaign(code)
                .then((c) => navigate(`/c/${c.code}`))
                .catch((e) => setError(errorText(e)))
            }
          >
            {ca.copyCampaign}
          </button>
        </>
      }
    >
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {campaign.finalReport && (
        <section className="card" aria-label={ca.finalReport}>
          <h2>
            {ca.finalReport} · {ca.verdicts[campaign.finalReport.verdict]}
          </h2>
          <p className="muted small">
            {campaign.finalReport.chapters} {ca.chapters.toLowerCase()} ·{' '}
            {campaign.finalReport.sectorsHeld}/{campaign.finalReport.sectorsTotal}{' '}
            {ca.sectors.toLowerCase()}
          </p>
        </section>
      )}
      <div className="split">
        <section className="cards">
          {board && (
            <article className="card" aria-label={ca.frontLine}>
              <div className="map" style={{ width: '100%', maxWidth: size, height: size }}>
                <img
                  src={terrainImageUrl(campaign.worldName)}
                  alt=""
                  width={size}
                  height={size}
                  draggable={false}
                />
                {board.sectors.map((s) => (
                  <span
                    key={s.name}
                    className={`marker sector ${s.control}`}
                    style={{ left: px(s.x), top: py(s.y) }}
                    title={`${s.name} · ${ca.sectorControl[s.control]}`}
                  >
                    {s.name.slice(0, 2)}
                  </span>
                ))}
              </div>
              <p className="muted small">
                {ca.enemyStrength}: <strong>{Math.round(board.enemyStrength * 100)}%</strong> ·{' '}
                {ca.civilianDisposition}:{' '}
                <strong>{ca.civilianNames(board.civilianDisposition)}</strong>
              </p>
              <ul className="plain small">
                {board.sectors.map((s) => (
                  <li key={s.name}>
                    {s.name}{' '}
                    <span className={`chip ${s.control}`}>{ca.sectorControl[s.control]}</span>
                  </li>
                ))}
              </ul>
            </article>
          )}
          {board && (
            <article className="card" aria-label={ca.campaignPage.territory}>
              <h2>{ca.campaignPage.territory}</h2>
              <dl className="facts territory">
                {territory.map((t) => (
                  <div key={t.key}>
                    <dt>{ca.sectorControl[t.key]}</dt>
                    <dd className="num">{t.count}</dd>
                  </div>
                ))}
              </dl>
            </article>
          )}
          <Planned ids={['campaign-front-line', 'campaign-cast-pins']} />
        </section>
        <aside className="side">
          {nextSessionChapter ? (
            <section className="card" aria-label={ca.campaignPage.nextSession}>
              <h2>{ca.campaignPage.nextSession}</h2>
              <p>
                <strong>{nextSessionChapter.missionTitle}</strong>{' '}
                <span className={`chip ${nextSessionChapter.sessionState === 'LIVE' ? 'on' : ''}`}>
                  {ca.sessionStates[nextSessionChapter.sessionState ?? 'SCHEDULED']}
                </span>
              </p>
              <Link className="button primary" to={`/s/${nextSessionChapter.sessionCode}`}>
                {ca.campaignPage.openSession}
              </Link>
              <PlannedChip id="campaign-next-session" />
            </section>
          ) : (
            <section className="card" aria-label={ca.campaignPage.nextSession}>
              <h2>{ca.campaignPage.nextSession}</h2>
              <p className="muted small">
                {canGenerate
                  ? ca.campaignPage.noNextSessionGenerate
                  : ca.campaignPage.noNextSession}
              </p>
            </section>
          )}
          {vote ? (
            <section className="card" aria-label={ca.campaignPage.nextVote}>
              <h2>{ca.campaignPage.nextVote}</h2>
              <p>{vote.question}</p>
              <ul className="plain small">
                {vote.options.map((o) => (
                  <li key={o}>
                    {o} · {vote.counts[o] ?? 0}
                  </li>
                ))}
              </ul>
              <Link to={`/s/${nextSessionCode}`}>{ca.campaignPage.goToDecide}</Link>
            </section>
          ) : (
            <section className="card" aria-label={ca.campaignPage.nextVote}>
              <h2>{ca.campaignPage.nextVote}</h2>
              <p className="muted small">{ca.campaignPage.nextVotePending}</p>
            </section>
          )}
          <article className="card" aria-label={ca.chapters}>
            <h2>{ca.chapters}</h2>
            {campaign.chapters.length === 0 && <p className="muted small">{ca.noChapters}</p>}
            <ol className="plain numbered">
              {campaign.chapters.map((c) => (
                <li key={c.ordinal}>
                  <Link to={`/m/${c.missionCode}`}>{c.missionTitle}</Link>{' '}
                  {c.sessionCode ? (
                    <Link to={`/s/${c.sessionCode}`} className="chip">
                      {ca.sessionStates[c.sessionState ?? 'SCHEDULED']}
                    </Link>
                  ) : (
                    <span className="chip">{ca.chapterPending}</span>
                  )}
                  {c.outcome && (
                    <span className={`chip ${c.outcome === 'success' ? 'on' : ''}`}>
                      {ca.outcomes[c.outcome] ?? c.outcome}
                    </span>
                  )}
                  {c.delta && <DeltaList delta={c.delta} />}
                </li>
              ))}
            </ol>
            {canGenerate && (
              <form
                className="row"
                onSubmit={(e) => {
                  e.preventDefault();
                  void generate();
                }}
              >
                <label>
                  {ca.nextChapterRecipe}
                  <select value={recipe} onChange={(e) => setRecipe(e.target.value)}>
                    <option value="">
                      {ca.fromState}
                      {campaign.suggestions.length
                        ? ` (${campaign.suggestions
                            .map((id) => recipes.find((r) => r.id === id)?.title ?? id)
                            .join(', ')})`
                        : ''}
                    </option>
                    {/* The wizard's rule (the owner, 2026-09-27): coming ones are shown, never picked. */}
                    {recipes.map((r) => (
                      <option key={r.id} value={r.id} disabled={isComing(r)}>
                        {r.title}
                        {isComing(r)
                          ? ` (${ca.missionUi.comingSoon})`
                          : isTesting(r)
                            ? ` (${ca.missionUi.testing})`
                            : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <button type="submit" className="primary" disabled={busy}>
                  {ca.nextChapter}
                </button>
              </form>
            )}
          </article>
          {board && board.threads.some((t) => !t.resolved) && (
            <section className="card" aria-label={ca.threads}>
              <h2>{ca.threads}</h2>
              <ul className="plain small">
                {board.threads
                  .filter((t) => !t.resolved)
                  .map((t) => (
                    <li key={t.id}>{t.text}</li>
                  ))}
              </ul>
            </section>
          )}
          {board && board.cast.length > 0 && (
            <section className="card" aria-label={ca.castMemory}>
              <h2>{ca.castMemory}</h2>
              <ul className="plain small">
                {board.cast.map((c) => (
                  <li key={c.id}>
                    <strong>{c.name}</strong> <span className="muted">{c.role}</span>{' '}
                    <span className="chip">{ca.castStatus[c.status]}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section className="card" aria-label={ca.standings}>
            <h2>{ca.standings}</h2>
            {campaign.standings.length === 0 && <p className="muted small">{ca.noStandings}</p>}
            <ul className="plain small">
              {campaign.standings.map((s) => (
                <li key={s.playerId}>
                  <strong>{s.name}</strong> <span className="chip">{s.rank}</span> · {s.attended}{' '}
                  {ca.attended} · {s.survived} {ca.survived}
                  {s.medals.length > 0 && (
                    <>
                      {' '}
                      {s.medals.map((m) => (
                        <span key={m} className="chip">
                          {m}
                        </span>
                      ))}
                    </>
                  )}
                </li>
              ))}
            </ul>
          </section>
          {campaign.canEdit && (
            <section className="card" aria-label={ca.visibility}>
              <label>
                {ca.visibility}
                <select
                  value={campaign.visibility}
                  onChange={(e) =>
                    void setCampaignVisibility(code, e.target.value as CampaignView['visibility'])
                      .then(setCampaign)
                      .catch((err) => setError(errorText(err)))
                  }
                >
                  {Object.entries(ca.visibilityNames).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
              <p className="muted small">
                {ca.nextDecidedBy}: {ca.nextDecidedByNames[campaign.nextDecidedBy]}
              </p>
            </section>
          )}
        </aside>
      </div>
    </Shell>
  );
}
